import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { LocationsApiService } from '@shared-features/services/locations-api.service';
import { getCoordInBetween, calculateMapZoom } from '@shared/utils';
import { isDefined } from '@shared/utils/is-defined.util';
import { GoogleMap } from '@angular/google-maps';
import { getDurationInDetails } from '@shared/utils/duration.util';
import { ConfigConstant } from '@shared/constants/config.constant';
import { MapElementDirective } from '@shared-features/directives/map-element.directive';
import { MapElement } from '@shared-features/enums';
import { Icon } from '@shared/models/icon.model';
import { GoogleRouteService } from '@shared-features/services/google-route.service';

@Component({
  selector: 'app-preview-map',
  templateUrl: './preview-map.component.html',
  styleUrls: ['./preview-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewMapComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() points: google.maps.LatLngLiteral[];
  @Input() ratio = 0.625;
  @Input() routeDrawingEnabled: boolean;
  @Input() icons: Record<number, Icon> = {};
  @Input() frame: 0 | 1 | 2 | 3 | 4 = 2;
  @ViewChild('googleMap') googleMap: GoogleMap;
  @ContentChildren(MapElementDirective) mapElements: QueryList<MapElementDirective>;
  customTemplates: Record<MapElement, TemplateRef<any>> = {} as unknown as Record<MapElement, TemplateRef<any>>;
  mapLoaded = false;
  zoom = 15;
  defaultLatLng = { lat: ConfigConstant.defaultLatitude, lng: ConfigConstant.defaultLongitude };
  mapCenter = this.defaultLatLng;
  directionService: google.maps.DirectionsService;
  distanceService: google.maps.DistanceMatrixService;
  startPoint: google.maps.LatLngLiteral;
  endPoint: google.maps.LatLngLiteral;
  polylinePoints: google.maps.LatLng[] = [];
  crowdedPolylinePoints: google.maps.LatLng[] = [];
  duration: { days: number; hours: number; mins: number; secs: number };
  distance: number;
  protected readonly MapElement = MapElement;
  constructor(private locationsApiService: LocationsApiService, protected changeDetectorRef: ChangeDetectorRef,private googleRouteService: GoogleRouteService) {}
  private static isPointDefined(coordinate: google.maps.LatLngLiteral): boolean {
    return isDefined(coordinate) && isDefined(coordinate.lat) && isDefined(coordinate.lng);
  }

  ngOnInit(): void {
    this.locationsApiService
      .loadGoogleMapsApi()
      .then(() => {
        this.mapLoaded = true;
        if (this.points.length && this.routeDrawingEnabled) {
          this.drawRoute();
        }
        this.changeDetectorRef.markForCheck();
      })
      .catch(console.error);
  }
  ngAfterViewInit() {
    this.mapElements?.forEach((element: MapElementDirective) => {
      this.customTemplates[element.type] = element.templateRef;
    });
  }
  ngOnChanges({ points }: SimpleChanges): void {
    if (points?.currentValue && this.points.length > 1) {
      if (
        PreviewMapComponent.isPointDefined(this.points[0]) &&
        PreviewMapComponent.isPointDefined(this.points[this.points.length - 1])
      ) {
        const pointA = this.points[0];
        const pointB = this.points[this.points.length - 1];
        this.zoom = calculateMapZoom(pointA, pointB);
        this.mapCenter = getCoordInBetween(pointA, pointB);
        this.startPoint = pointA;
        this.endPoint = pointB;
        if (this.mapLoaded && this.routeDrawingEnabled) {
          this.drawRoute();
        }
        this.changeDetectorRef.markForCheck();
      }
    }
  }
  private drawRoute(): void {
    this.googleRouteService.drawRoute(this.points).subscribe((data) => {
      this.polylinePoints = data.polylinePoints;
      this.duration = getDurationInDetails(data.durationInTraffic || data.duration);
      this.distance = data.distance;
    });
    this.changeDetectorRef.markForCheck();
  }
}